import { getTargetPlayer } from './targetPlayer';
import React, { useState, useEffect, useRef, useMemo, useCallback, ChangeEvent } from 'react';
import styled from 'styled-components';
import { Message } from '../../types/chat';
import { io, Socket } from 'socket.io-client';
import { MessageList } from '../chat/MessageList';
import { FilesView } from './FilesView';

interface SelectedUser {
  id: string;
  name: string;
}

interface AdminPanelProps {
  adminToken: string;
}

interface AdminSession {
  _id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  email?: string;
  playerEmail?: string;
  lastMessage: string;
  lastTimestamp: string;
  unreadCount?: number;
  isArchived?: boolean;
  lastClientIp?: string;
  firstMessageAt?: string;
  lastMessageAt?: string;
  retentionStatus?: 'none' | 'auto_redacted' | 'manual_redacted';
  retentionRedactedAt?: string;
}

interface LimitConsentCaseSummary {
  caseId: string;
  status: string;
  currentAttempt: number;
  playerId: string;
  playerAccountId: string;
  playerDisplayName?: string | null;
  sessionId: string;
  requestedLimit?: {
    amount: number;
    period: 'day' | 'week' | 'month';
    currency?: string;
  } | null;
  requiresProof: boolean;
  isIncrease: boolean;
  nextQuestion?: {
    questionKey: string;
    questionText: string;
  } | null;
  updatedAt: string;
  createdAt: string;
}

const REVIEWABLE_CONSENT_STATUSES = new Set([
  'in_chat_questionnaire',
  'pending_review',
]);

const translateConsentStatus = (status: string) => {
  switch (status) {
    case 'in_chat_questionnaire':
      return 'In chat-vragenlijst';
    case 'pending_review':
      return 'Wacht op beoordeling';
    case 'approved':
      return 'Goedgekeurd';
    case 'rejected':
      return 'Afgewezen';
    case 'declined_by_player':
      return 'Geweigerd door speler';
    case 'archived_superseded':
      return 'Gearchiveerd';
    default:
      return status || '-';
  }
};

const STATIC_URL = process.env.REACT_APP_API_URL || (globalThis as any).supportlyChatConfig?.staticUrl || '';

const SOCKET_URL1 = process.env.REACT_APP_API_URL || (globalThis as any).supportlyChatConfig?.socketUrl || '/';

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #f8f9fa;
  z-index: 2000;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  flex: 1;
  padding: 1rem;
  overflow: hidden;
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 1rem;
  background: #f1f5f9;
  min-height: 0;
`;

const TopNavigation = styled.nav`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.65rem;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.06);
`;

const TopNavigationButton = styled.button<{ active?: boolean }>`
  background: ${({ active }) => (active ? '#0f172a' : '#ffffff')};
  color: ${({ active }) => (active ? '#f8fafc' : '#334155')};
  border: 1px solid ${({ active }) => (active ? '#0f172a' : '#cbd5e1')};
  border-radius: 0.65rem;
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.92rem;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    background: ${({ active }) => (active ? '#0b1324' : '#f8fafc')};
    border-color: ${({ active }) => (active ? '#0b1324' : '#94a3b8')};
  }
`;

const Sidebar = styled.div`
  background: linear-gradient(180deg, #131a24 0%, #1a2432 100%);
  border-radius: 0.5rem;
  padding: 1.5rem 1rem 1rem 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  color: #fff;
  min-width: 220px;
`;

const SidebarTitle = styled.h2`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
  letter-spacing: 0.02em;
`;

const SidebarSection = styled.div`
  margin-bottom: 2rem;
`;

const SidebarList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SidebarListItem = styled.li`
  margin-bottom: 10px;
`;

const SidebarButton = styled.button<{ selected?: boolean }>`
  background: ${({ selected }) => selected ? '#007bff' : 'transparent'};
  color: ${({ selected }) => selected ? '#fff' : '#b0b8c1'};
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: background 0.15s, color 0.15s;
  position: relative;
  &:hover {
    background: #2c3240;
    color: #fff;
  }
`;

const MainContent = styled.div`
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 2rem 2.5rem 2rem 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  min-height: 0;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const StatsTopBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const TopStatCard = styled.div`
  background: #0f172a;
  color: #fff;
  border-radius: 10px;
  padding: 0.45rem 0.6rem;
  border: 1px solid #1e293b;
`;

const TopStatLabel = styled.div`
  font-size: 0.68rem;
  color: #cbd5e1;
`;

const TopStatValue = styled.div`
  margin-top: 0.1rem;
  font-size: 1rem;
  font-weight: 800;
  color: #f8fafc;
`;

const MessageArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const PlayerSearchInput = styled.input`
  width: 100%;
  margin-bottom: 8px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
  padding: 6px;
`;

const PlayerListScroll = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const SessionName = styled.div`
  font-weight: 600;
  font-size: 15px;
`;

const SessionIdText = styled.div`
  font-size: 12px;
  color: #cbd5e1;
  margin-bottom: 2px;
`;

const SidebarUnreadBadge = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  background: #2563eb;
  color: #fff;
  border-radius: 12px;
  font-size: 12px;
  padding: 2px 8px;
  font-weight: 700;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HeaderTitle = styled.h2`
  margin: 0;
`;

const HeaderLink = styled.a`
  color: #2563eb;
  text-decoration: underline;
  font-weight: 700;
`;

const HeaderMetaText = styled.span`
  font-size: 13px;
  color: #64748b;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NeutralButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #94a3b8;
  background: #fff;
  cursor: pointer;
`;

const SoundToggleButton = styled.button<{ enabled: boolean }>`
  background: none;
  border: none;
  color: #007bff;
  font-size: 1.5rem;
  cursor: pointer;
  margin-left: 8px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  opacity: ${({ enabled }) => (enabled ? 1 : 0.5)};
`;

const ConsentLayout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 12px;
  min-height: 500px;
`;

const ConsentListPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  overflow-y: auto;
  max-height: 620px;
`;

const ConsentFilterGroup = styled.div`
  margin-bottom: 10px;
`;

const ConsentFilterLabel = styled.label`
  font-size: 12px;
  color: #475569;
`;

const ConsentFilterSelect = styled.select`
  width: 100%;
  margin-top: 4px;
  padding: 6px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
`;

const MutedText = styled.div`
  color: #64748b;
`;

const ConsentCaseButton = styled.button<{ selected: boolean }>`
  width: 100%;
  text-align: left;
  margin-bottom: 8px;
  border-radius: 8px;
  border: ${({ selected }) => (selected ? '1px solid #2563eb' : '1px solid #e2e8f0')};
  background: ${({ selected }) => (selected ? '#eff6ff' : '#fff')};
  padding: 10px;
  cursor: pointer;
`;

const ConsentCaseTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
`;

const ConsentCaseId = styled.div`
  font-size: 11px;
  color: #64748b;
`;

const ConsentCaseStatus = styled.div`
  font-size: 12px;
  margin-top: 4px;
`;

const ConsentDetailPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
`;

const ConsentTitle = styled.h3`
  margin-top: 0;
`;

const ConsentDetailRow = styled.div`
  font-size: 13px;
  color: #334155;
  margin-bottom: 8px;
`;

const ConsentWarning = styled.div`
  margin-top: 14px;
  padding: 10px;
  border-radius: 8px;
  background: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
  font-size: 12px;
`;

const ConsentReasonTextarea = styled.textarea`
  width: 100%;
  margin-top: 10px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  padding: 10px;
`;

const ConsentActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

type ConsentButtonVariant = 'approve' | 'reject' | 'refer' | 'neutral' | undefined;

function getConsentButtonBorder(variant: ConsentButtonVariant): string {
  if (variant === 'approve' || variant === 'reject') return 'none';
  if (variant === 'refer') return '1px solid #c2410c';
  return '1px solid #94a3b8';
}

function getConsentButtonBackground(variant: ConsentButtonVariant): string {
  if (variant === 'approve') return '#16a34a';
  if (variant === 'reject') return '#dc2626';
  if (variant === 'refer') return '#fff7ed';
  return '#fff';
}

function getConsentButtonColor(variant: ConsentButtonVariant): string {
  if (variant === 'approve' || variant === 'reject') return '#fff';
  if (variant === 'refer') return '#9a3412';
  return '#0f172a';
}

const ConsentActionButton = styled.button<{ variant?: 'approve' | 'reject' | 'refer' | 'neutral' }>`
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: ${({ variant }) => (variant === 'refer' ? 600 : 500)};
  border: ${({ variant }) => getConsentButtonBorder(variant)};
  background: ${({ variant }) => getConsentButtonBackground(variant)};
  color: ${({ variant }) => getConsentButtonColor(variant)};
`;

const ArchiveSection = styled.div`
  padding: 8px;
`;

const ArchiveList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ArchiveListItem = styled.li`
  margin-bottom: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
`;

const ArchiveSessionName = styled.div`
  font-weight: 700;
`;

const ArchiveSessionId = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const ArchiveActions = styled.div`
  margin-top: 8px;
`;

const ArchiveWarning = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #fed7aa;
  background: #fff7ed;
  color: #9a3412;
  font-size: 12px;
`;

const ArchiveStatusBadge = styled.span<{ kind: 'auto' | 'manual' }>`
  display: inline-block;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ kind }) => (kind === 'auto' ? '#dbeafe' : '#dcfce7')};
  color: ${({ kind }) => (kind === 'auto' ? '#1d4ed8' : '#166534')};
`;

const ArchiveExportButton = styled(NeutralButton)`
  margin-left: 8px;
`;

const ArchiveRedactButton = styled(NeutralButton)`
  margin-left: 8px;
  border-color: #c2410c;
  color: #9a3412;
`;

const UnreadSection = styled.div`
  padding: 32px;
`;

const EmptyUnreadText = styled.div`
  color: #888;
  font-size: 16px;
`;

const UnreadList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const UnreadListItem = styled.li`
  margin-bottom: 14px;
`;

const UnreadSessionButton = styled.button`
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 12px 18px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #23272f;
`;

const UnreadCountBadge = styled.span`
  background: #2563eb;
  color: #fff;
  border-radius: 12px;
  font-size: 13px;
  padding: 2px 10px;
  font-weight: 700;
`;

const ChatComposer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const ChatMessageInput = styled.input`
  flex: 1;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #ccc;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FilePickerLabel = styled.label`
  background: #f0f0f0;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  border: 1px solid #ccc;
`;

const PrimaryButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  background: #007bff;
  color: #fff;
  border: none;
  cursor: pointer;
`;

const SelectedFileInfo = styled.div`
  margin-top: 8px;
  color: #007bff;
`;

const RemoveFileButton = styled.button`
  margin-left: 8px;
  color: #dc3545;
  background: none;
  border: none;
  cursor: pointer;
`;

const PlayersDirectory = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
`;

const PlayersFiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
`;

const PlayerDirectoryInput = styled.input`
  width: 100%;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
`;

const PlayersDirectoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 520px;
  overflow-y: auto;
`;

const PlayersDirectoryItem = styled.li`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const PlayersDirectoryMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const ArchivedPlayerBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid #cbd5e1;
  font-size: 10px;
  color: #475569;
  background: #f8fafc;
  font-weight: 700;
  text-transform: uppercase;
`;

export const AdminPanel: React.FC<AdminPanelProps> = ({ adminToken }) => {
  const [stats, setStats] = useState({
    totalMessages: 0,
    activeUsers: 0,
    messagesToday: 0,
    averageResponseTime: 0,
    totalSessions: 0,
    archivedSessions: 0,
    unreadTotal: 0,
    filesTotal: 0,
  });
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(() => getTargetPlayer(window.location.search));
  const targetPlayerLoaded = useRef(false);
  const [adminMessage, setAdminMessage] = useState('');
  const [allMessages, setAllMessages] = useState<Message[]>([]);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const originalTitle = useRef(document.title);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('adminSoundEnabled');
    return stored === null ? true : stored === 'true';
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatTitleRef = useRef<HTMLHeadingElement>(null);
  const [isAdminSending, setIsAdminSending] = useState(false);
  const [search, setSearch] = useState('');
  const [playerIdSearch, setPlayerIdSearch] = useState('');
  const [playerNameSearch, setPlayerNameSearch] = useState('');
  const [playerEmailSearch, setPlayerEmailSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(20);
  const [page, setPage] = useState<Record<string, number>>({});
  const [hasMore, setHasMore] = useState<Record<string, boolean>>({});
  const limit = 40;
  const [activeSection, setActiveSection] = useState<'chat' | 'files' | 'archive' | 'consent' | 'players'>('chat');
  const [consentCases, setConsentCases] = useState<LimitConsentCaseSummary[]>([]);
  const [selectedConsentCaseId, setSelectedConsentCaseId] = useState<string | null>(null);
  const [consentReason, setConsentReason] = useState('');
  const [consentActionLoading, setConsentActionLoading] = useState(false);
  const [consentStatusFilter, setConsentStatusFilter] = useState<string>('all');
  const isFetchingStats = useRef(false);
  const isFetchingMessages = useRef(false);
  const isFetchingSessions = useRef(false);
  const authHeaders = useMemo<HeadersInit | undefined>(
    () => (adminToken ? { Authorization: `Bearer ${adminToken}` } : undefined),
    [adminToken]
  );

  const fetchConsentCases = useCallback(() => {
    if (!adminToken) return;
    const consentQueryMap: Record<string, string> = {
      active: '?status=in_chat_questionnaire',
      review: '?status=pending_review',
      approved: '?status=approved',
      rejected: '?status=rejected',
    };
    const query = consentQueryMap[consentStatusFilter] ?? '';

    fetch(`${SOCKET_URL1}/api/admin/limit-consent${query}`, {
      headers: authHeaders,
    })
      .then((res) => res.json())
      .then((data) => {
        const list = (Array.isArray(data?.cases) ? data.cases : []).sort(
          (a: LimitConsentCaseSummary, b: LimitConsentCaseSummary) => {
            const aTs = new Date(a.updatedAt || a.createdAt || 0).getTime();
            const bTs = new Date(b.updatedAt || b.createdAt || 0).getTime();
            return bTs - aTs;
          }
        );
        setConsentCases(list);
        if (list.length === 0) {
          setSelectedConsentCaseId(null);
        } else if (!selectedConsentCaseId || !list.some((item: LimitConsentCaseSummary) => item.caseId === selectedConsentCaseId)) {
          setSelectedConsentCaseId(list[0].caseId);
        }
      })
      .catch(() => setConsentCases([]));
  }, [adminToken, authHeaders, consentStatusFilter, selectedConsentCaseId]);

  useEffect(() => {
    if (!adminToken) {
      return;
    }

    const socket = io(SOCKET_URL1, {
      auth: { userId: 'admin', name: 'Admin', isAnonymous: false, adminToken },
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      withCredentials: false
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[SOCKET] Connected! Socket ID:', socket.id, 'Connected:', socket.connected);
    });
    socket.on('disconnect', (reason) => {
      console.log('[SOCKET] Disconnected!', reason);
    });
    socket.on('connect_error', (err) => {
      console.log('[SOCKET] Connection error:', err);
    });
    socket.onAny((event, ...args) => {
      console.log(`[SOCKET] Event: ${event}`, ...args);
    });

    socket.emit('join_admin');
    console.log('Emitted join_admin event');

    socket.on('sessions_update', (data) => {
      if (data.sessions) {
        const sortedSessions = data.sessions.sort(
          (a: { lastTimestamp: string }, b: { lastTimestamp: string }) =>
            new Date(b.lastTimestamp).getTime() - new Date(a.lastTimestamp).getTime()
        );
        setSessions(sortedSessions);
      }
    });

    socket.on('stats_update', (data) => {
      setStats((prev) => ({ ...prev, ...data }));
    });

    const handleNewMessage = (message: Message) => {
      setAllMessages(prev => {
        const getSignature = (msg: any) => {
          return [
            msg.id || msg._id || '',
            msg.timestamp || '',
            msg.sessionId || '',
            msg.fileName || '',
            msg.content || ''
          ].join('|');
        };
        const existingSignatures = new Set(prev.map(getSignature));
        const newMsgSignature = getSignature(message);
        if (existingSignatures.has(newMsgSignature)) {
          return prev;
        }
        return [...prev, { ...message }];
      });
      if (message.sender !== 'admin') {
        if (audioRef.current && soundEnabled) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(err => console.error('Error playing sound:', err));
        }
        if (!document.hasFocus()) {
          setHasNewMessage(true);
        }
      }
    };

    socket.on('new_message', handleNewMessage);

    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [adminToken, soundEnabled, authHeaders]);

  useEffect(() => {
    if (isFetchingMessages.current) return;
    isFetchingMessages.current = true;
    fetch(`${SOCKET_URL1}/api/messages`, {
      headers: authHeaders
    })
      .then(res => res.json())
      .then(data => {
        const getSignature = (msg: any) => [
          msg.id || msg._id || '',
          msg.timestamp || '',
          msg.sessionId || '',
          msg.fileName || '',
          msg.content || ''
        ].join('|');
        const seen = new Set<string>();
        const filtered = (Array.isArray(data) ? data : data.messages).filter((msg: any) => {
          const sig = getSignature(msg);
          if (seen.has(sig)) return false;
          seen.add(sig);
          return true;
        });
        setAllMessages(filtered);
      })
      .catch(() => setAllMessages([]))
      .finally(() => { isFetchingMessages.current = false; });
  }, [authHeaders]);

  useEffect(() => {
    if (isFetchingStats.current) return;
    isFetchingStats.current = true;
    fetch(`${SOCKET_URL1}/api/admin/stats`, {
          headers: authHeaders
    })
      .then(res => res.json())
      .then(statsData => setStats((prev) => ({ ...prev, ...statsData })))
      .catch((error) => {
        console.error('Error fetching admin data:', error);
      })
      .finally(() => { isFetchingStats.current = false; });
  }, [authHeaders]);

  useEffect(() => {
    if (isFetchingSessions.current) return;
    isFetchingSessions.current = true;
    fetch(`${SOCKET_URL1}/api/messages/sessions?includeArchived=1`, {
      headers: authHeaders
    })
      .then(res => res.json())
      .then(data => setSessions(data))
      .catch(() => setSessions([]))
      .finally(() => { isFetchingSessions.current = false; });
  }, [authHeaders]);

  useEffect(() => {
    fetchConsentCases();
  }, [fetchConsentCases]);

  const chatMessages = useMemo(() => {
    if (!selectedUser) return [];
    return allMessages.filter(m => m.sessionId === selectedUser.id)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [allMessages, selectedUser]);

  const fetchMessagesForUser = useCallback((userId: string, pageNum = 1) => {
    fetch(`${SOCKET_URL1}/api/messages?sessionId=${userId}&page=${pageNum}&limit=${limit}`, {
      headers: authHeaders
    })
      .then(res => res.json())
      .then(data => setAllMessages(prev => {
        const msgs = Array.isArray(data) ? data : data.messages;
        const filtered = prev.filter(m => m.sessionId !== userId);
        if (pageNum === 1) {
          setHasMore(hm => ({ ...hm, [userId]: (msgs.length === limit) }));
          setPage(pg => ({ ...pg, [userId]: 1 }));
          return [...filtered, ...msgs];
        } else {
          setHasMore(hm => ({ ...hm, [userId]: (msgs.length === limit) }));
          setPage(pg => ({ ...pg, [userId]: pageNum }));
          // Добавляем новые сообщения в начало
          const prevMsgs = prev.filter(m => m.sessionId === userId);
          return [...filtered, ...msgs, ...prevMsgs];
        }
      }))
      .catch(() => {});
  }, [authHeaders]);

  useEffect(() => {
    const target = getTargetPlayer(window.location.search);
    if (!adminToken || !target || targetPlayerLoaded.current) return;
    targetPlayerLoaded.current = true;
    fetchMessagesForUser(target.id);
  }, [adminToken, fetchMessagesForUser]);
  const loadMoreMessages = () => {
    if (!selectedUser) return;
    const currPage = page[selectedUser.id] || 1;
    if (hasMore[selectedUser.id] === false) return;
    fetchMessagesForUser(selectedUser.id, currPage + 1);
  };

  useEffect(() => {
    if (selectedUser) {
      fetch(`${SOCKET_URL1}/api/messages/mark-read?sessionId=${selectedUser.id}`, {
        method: 'POST',
        headers: authHeaders
      }).then(() => {
        setAllMessages(prev => prev.map(msg =>
          msg.sessionId === selectedUser.id && msg.sender !== 'admin'
            ? { ...msg, status: 'read' }
            : msg
        ));
        fetch(`${SOCKET_URL1}/api/messages/sessions?includeArchived=1`, {
          headers: authHeaders
        })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setSessions(data);
          } else if (data.sessions) {
            setSessions(data.sessions);
          }
        });
      });
    }
  }, [selectedUser, authHeaders]);

  const handleSendToUser = async () => {
    if (!selectedUser || (!adminMessage.trim() && !selectedFile)) return;
    setSending(true);
    setIsAdminSending(true);

    const socket = socketRef.current;
    if (!socket?.connected) {
      alert('Socket niet verbonden. Bericht is niet verzonden.');
      setSending(false);
      setIsAdminSending(false);
      return;
    }

    try {
      console.log('=== Admin Send Message Start ===');
      console.log('Selected User:', selectedUser);
      console.log('Socket ID:', socket.id);
      console.log('Socket Connected:', socket.connected);

      const message: Partial<Message> = {
        content: adminMessage.trim(),
        senderName: 'Admin',
        sender: 'admin',
        timestamp: new Date().toISOString(),
        status: 'sending',
        sessionId: selectedUser.id
      };

      if (!message.content && !selectedFile) {
        setSending(false);
        setIsAdminSending(false);
        return;
      }

      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('message', JSON.stringify(message));

        console.log('[ADMIN] Attempting to send file via API:', formData);
        const response = await fetch(`${SOCKET_URL1}/api/messages/upload`, {
          method: 'POST',
          body: formData,
          headers: authHeaders
        });

        if (!response.ok) {
          throw new Error('File upload failed');
        }

        const data = await response.json();
        console.log('[ADMIN] File upload response:', data);
        
        socket.emit('send_message', data);
        setSelectedFile(null);
        const fileInput = document.getElementById('admin-file-input') as HTMLInputElement | null;
        if (fileInput) fileInput.value = '';
        setAdminMessage('');
        setSending(false);
        setIsAdminSending(false);
        return;
      }

      console.log('[ADMIN] Attempting to send text message via socket:', message);
      socket.emit('send_message', message, (ack: any) => {
        console.log('[ADMIN] Server ack after send:', ack);
        if (ack?.error) {
          alert('Fout bij verzenden van bericht: ' + ack.error);
        }
      });
      setAdminMessage('');
    } catch (error) {
      console.error('[ADMIN] Error sending message:', error);
      alert('Verzenden van bericht mislukt.');
    } finally {
      setSending(false);
      setIsAdminSending(false);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  console.log('chatMessages:', chatMessages);
  console.log('allMessages:', allMessages);
  console.log('selectedUser:', selectedUser);

  useEffect(() => {
    if (selectedUser && socketRef.current) {
      socketRef.current.emit('join', selectedUser.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    let blinkInterval: NodeJS.Timeout | null = null;
    if (hasNewMessage) {
      blinkInterval = setInterval(() => {
        document.title = document.title === originalTitle.current ? '🔔 New message!' : originalTitle.current;
      }, 1000);
    } else {
      document.title = originalTitle.current;
    }

    return () => {
      if (blinkInterval) {
        clearInterval(blinkInterval);
      }
    };
  }, [hasNewMessage]);

  useEffect(() => {
    const onFocus = () => setHasNewMessage(false);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const toggleSound = () => {
    setSoundEnabled((prev) => {
      localStorage.setItem('adminSoundEnabled', String(!prev));
      return !prev;
    });
  };

  useEffect(() => {
    if (isAdminSending) return;
    if (chatTitleRef.current) {
      chatTitleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedUser, isAdminSending]);

  const getSessionUserName = useCallback((session: any) => {
    return session.userName || session.senderName ||
      allMessages.find(m => m.sessionId === session._id && m.senderName)?.senderName ||
      session.userId || session._id;
  }, [allMessages]);

  const getSessionEmail = useCallback((session: AdminSession) => {
    return (
      session.userEmail ||
      session.email ||
      session.playerEmail ||
      ''
    );
  }, []);

  const sortedSessions = useMemo(() => {
    return [...sessions].sort(
      (a, b) => new Date(b.lastTimestamp).getTime() - new Date(a.lastTimestamp).getTime()
    );
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    return sortedSessions.filter(session => {
      const byArchive =
        activeSection === 'archive' ? session.isArchived : !session.isArchived;
      const term = search.trim().toLowerCase();
      const bySearch =
        term.length === 0
          ? true
          : [
              getSessionUserName(session),
              session._id,
              session.userId,
              getSessionEmail(session),
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase()
              .includes(term);
      return byArchive && bySearch;
    });
  }, [sortedSessions, search, activeSection, getSessionUserName, getSessionEmail]);

  const filteredPlayersDirectory = useMemo(() => {
    const idTerm = playerIdSearch.trim().toLowerCase();
    const nameTerm = playerNameSearch.trim().toLowerCase();
    const emailTerm = playerEmailSearch.trim().toLowerCase();

    return sortedSessions.filter((session) => {
      const idMatch =
        !idTerm ||
        `${session._id} ${session.userId || ''}`.toLowerCase().includes(idTerm);
      const nameMatch =
        !nameTerm || getSessionUserName(session).toLowerCase().includes(nameTerm);
      const emailMatch =
        !emailTerm || getSessionEmail(session).toLowerCase().includes(emailTerm);
      return idMatch && nameMatch && emailMatch;
    });
  }, [
    sortedSessions,
    playerIdSearch,
    playerNameSearch,
    playerEmailSearch,
    getSessionUserName,
    getSessionEmail,
  ]);

  const visibleSessions = filteredSessions.slice(0, visibleCount);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setVisibleCount(c => c + 10);
    }
  };

  const sessionsWithUnread = useMemo(() => {
    return sessions.filter(session => (session.unreadCount ?? 0) > 0);
  }, [sessions]);

  const selectedSessionMeta = useMemo(
    () => sessions.find((s) => s._id === selectedUser?.id),
    [sessions, selectedUser]
  );

  const selectedConsentCase = useMemo(
    () => consentCases.find((item) => item.caseId === selectedConsentCaseId) || null,
    [consentCases, selectedConsentCaseId]
  );

  const totalUnreadInSidebar = useMemo(
    () => sessions.reduce((acc, s) => acc + (s.unreadCount || 0), 0),
    [sessions]
  );

  const hasPlayerAccount = (name?: string) => {
    if (!name) return false;
    return !/(^guest$|^gast$)/i.test(name.trim());
  };

  const getPlayerAdminUrl = (playerId: string) => {
    const fallbackPath = '/admin/player/view/';
    const configured = process.env.REACT_APP_LINK_SITE || '';

    const getConfiguredPath = () => {
      if (!configured) return fallbackPath;
      try {
        const parsed = new URL(configured);
        return parsed.pathname || fallbackPath;
      } catch {
        return configured.startsWith('/') ? configured : fallbackPath;
      }
    };

    const parentOrigin = (() => {
      if (typeof document === 'undefined' || !document.referrer) return '';
      try {
        return new URL(document.referrer).origin;
      } catch {
        return '';
      }
    })();

    const origin = parentOrigin || globalThis.location.origin;
    const path = getConfiguredPath().endsWith('/')
      ? getConfiguredPath()
      : `${getConfiguredPath()}/`;

    return `${origin}${path}${encodeURIComponent(playerId)}`;
  };

  const handleArchiveSession = async (sessionId: string, archive: boolean) => {
    const refreshSessions = () =>
      fetch(`${SOCKET_URL1}/api/messages/sessions?includeArchived=1`, { headers: authHeaders })
        .then((r) => r.json())
        .then((data) => setSessions(Array.isArray(data) ? data : data.sessions || []));

    const refreshSessionMessages = (targetSessionId: string) => {
      if (selectedUser?.id === targetSessionId) {
        fetchMessagesForUser(targetSessionId);
      }
    };

    try {
      const response = await fetch(`${SOCKET_URL1}/api/admin/archive-session`, {
        method: 'POST',
        headers: {
          ...(authHeaders as object),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId, archive }),
      });
      if (!response.ok) throw new Error('archive-failed');
      await refreshSessions();
      refreshSessionMessages(sessionId);
    } catch (error) {
      console.warn('[AdminPanel] Archive session error:', error);
      alert('Archiveren van chat is mislukt.');
    }
  };

  const getSessionReferenceDate = (session: AdminSession): Date | null => {
    const value = session.lastMessageAt || session.lastTimestamp || session.firstMessageAt;
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const isSessionOlderThanRetention = (session: AdminSession): boolean => {
    const referenceDate = getSessionReferenceDate(session);
    if (!referenceDate) return false;
    const cutoff = Date.now() - 365 * 24 * 60 * 60 * 1000;
    return referenceDate.getTime() < cutoff;
  };

  const handleRedactArchivedSession = async (session: AdminSession) => {
    const reasonInput = globalThis.prompt(
      'Reden voor redactie (verplicht voor AVG/GDPR audittrail):',
      'Dataretentie > 1 jaar, redactie conform AVG/GDPR dataminimalisatie.'
    );
    if (!reasonInput) return;
    const reason = reasonInput.trim();
    if (!reason) {
      alert('Reden is verplicht voor redactie.');
      return;
    }

    const confirmed = globalThis.confirm(
      'Deze actie redigeert alle berichten en bestanden in deze gearchiveerde chat. Doorgaan?'
    );
    if (!confirmed) return;

    try {
      const response = await fetch(`${SOCKET_URL1}/api/admin/redact-session`, {
        method: 'POST',
        headers: {
          ...(authHeaders as object),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId: session._id, reason }),
      });
      if (!response.ok) throw new Error('redact-failed');
      const refreshed = await fetch(`${SOCKET_URL1}/api/messages/sessions?includeArchived=1`, {
        headers: authHeaders,
      });
      const refreshedData = await refreshed.json();
      setSessions(Array.isArray(refreshedData) ? refreshedData : refreshedData.sessions || []);
      if (selectedUser?.id === session._id) {
        fetchMessagesForUser(session._id);
      }
    } catch (error) {
      console.warn('[AdminPanel] Redact session error:', error);
      alert('Redigeren van gearchiveerde chat is mislukt.');
    }
  };

  const handleExportSession = async (sessionId: string) => {
    try {
      const response = await fetch(`${SOCKET_URL1}/api/admin/export/${encodeURIComponent(sessionId)}?format=txt`, {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error('export-failed');
      const blob = await response.blob();
      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-export-${sessionId}.txt`;
      a.click();
      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('[AdminPanel] Export session error:', error);
      alert('Exporteren van chat is mislukt.');
    }
  };

  const runConsentAction = async (
    action: 'approve' | 'reject' | 'restart' | 'refer_net_deposit'
  ) => {
    if (!selectedConsentCase) return;
    const trimmedReason = consentReason.trim();
    if (!trimmedReason) {
      alert(
        'Opmerking is verplicht voor goedkeuren/afwijzen/herstarten/verwijzen.'
      );
      return;
    }

    setConsentActionLoading(true);
    try {
      if (action === 'restart') {
        const response = await fetch(
          `${SOCKET_URL1}/api/admin/limit-consent/${encodeURIComponent(selectedConsentCase.caseId)}/restart`,
          {
            method: 'POST',
            headers: {
              ...(authHeaders as object),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reason: trimmedReason,
              requestedLimit: selectedConsentCase.requestedLimit,
              isIncrease: selectedConsentCase.isIncrease,
              requiresProof: selectedConsentCase.requiresProof,
            }),
          }
        );
        if (!response.ok) throw new Error('restart_failed');
      } else if (action === 'refer_net_deposit') {
        const response = await fetch(
          `${SOCKET_URL1}/api/admin/limit-consent/${encodeURIComponent(selectedConsentCase.caseId)}/refer-net-deposit-limit`,
          {
            method: 'POST',
            headers: {
              ...(authHeaders as object),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              reason: trimmedReason,
            }),
          }
        );
        if (!response.ok) throw new Error('refer_net_deposit_failed');
      } else {
        const response = await fetch(
          `${SOCKET_URL1}/api/admin/limit-consent/${encodeURIComponent(selectedConsentCase.caseId)}/decision`,
          {
            method: 'POST',
            headers: {
              ...(authHeaders as object),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              decision: action === 'approve' ? 'approved' : 'rejected',
              reason: trimmedReason,
            }),
          }
        );
        if (!response.ok) throw new Error('decision_failed');
      }

      setConsentReason('');
      fetchConsentCases();
    } catch (error) {
      console.warn('[AdminPanel] Consent action error:', error);
      alert('Actie op consent-case is mislukt.');
    } finally {
      setConsentActionLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser && chatMessages.length > 0) {
      const unread = chatMessages.filter(
        m => m.sender !== 'admin' && m.status !== 'read'
      );
      if (unread.length > 0) {
        fetch(`${SOCKET_URL1}/api/messages/mark-read?sessionId=${selectedUser.id}`, {
          method: 'POST',
          headers: authHeaders
        });
        setAllMessages(msgs =>
          msgs.map(m =>
            m.sessionId === selectedUser.id && m.sender !== 'admin' && m.status !== 'read'
              ? { ...m, status: 'read' }
              : m
          )
        );
      }
    }
  }, [selectedUser, chatMessages, authHeaders]);

  if (!adminToken) {
    return <Container>Admin token ontbreekt.</Container>;
  }

  const renderHeaderTitle = (): React.ReactNode => {
    if (activeSection === 'files') {
      if (!selectedUser) return 'Alle bestanden';
      return (
        <>
          Spelerbestanden: {hasPlayerAccount(selectedUser.name) ? (
            <HeaderLink
              href={getPlayerAdminUrl(selectedUser.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {selectedUser.name}
            </HeaderLink>
          ) : selectedUser.name} <HeaderMetaText>({selectedUser.id}{selectedSessionMeta?.lastClientIp ? ` | IP ${selectedSessionMeta.lastClientIp}` : ''})</HeaderMetaText>
        </>
      );
    }
    if (activeSection === 'archive') return 'Gearchiveerde chats';
    if (activeSection === 'players') return 'Spelersoverzicht';
    if (activeSection === 'consent') return 'Limiet-consent dossiers';
    if (!selectedUser) return 'Laatste berichten';
    return (
      <>
        Chat met speler: {hasPlayerAccount(selectedUser.name) ? (
          <HeaderLink
            href={getPlayerAdminUrl(selectedUser.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {selectedUser.name}
          </HeaderLink>
        ) : selectedUser.name} <HeaderMetaText>({selectedUser.id}{selectedSessionMeta?.lastClientIp ? ` | IP ${selectedSessionMeta.lastClientIp}` : ''})</HeaderMetaText>
      </>
    );
  };

  const renderActiveSection = (): React.ReactNode => {
    if (activeSection === 'files') {
      return (
        <FilesView
          selectedUserId={selectedUser?.id}
          adminToken={adminToken}
          setSelectedUser={setSelectedUser}
          fetchMessagesForUser={fetchMessagesForUser}
          setActiveSection={setActiveSection}
        />
      );
    }
    if (activeSection === 'consent') {
      return (
        <ConsentLayout className="admin-consent-layout">
          <ConsentListPanel className="admin-consent-list-panel">
            <ConsentFilterGroup>
              <ConsentFilterLabel>Filter status</ConsentFilterLabel>
              <ConsentFilterSelect
                value={consentStatusFilter}
                onChange={(e) => setConsentStatusFilter(e.target.value)}
              >
                <option value="active">In chat</option>
                <option value="review">Wacht op beoordeling</option>
                <option value="approved">Goedgekeurd</option>
                <option value="rejected">Afgewezen</option>
                <option value="all">Alle</option>
              </ConsentFilterSelect>
            </ConsentFilterGroup>
            {consentCases.length === 0 ? (
              <MutedText>Geen consent-cases gevonden.</MutedText>
            ) : (
              consentCases.map((item) => (
                <ConsentCaseButton
                  className="admin-consent-case-button"
                  key={item.caseId}
                  selected={selectedConsentCaseId === item.caseId}
                  onClick={() => setSelectedConsentCaseId(item.caseId)}
                >
                  <ConsentCaseTitle>{item.playerDisplayName || item.playerId}</ConsentCaseTitle>
                  <ConsentCaseId>{item.caseId}</ConsentCaseId>
                  <ConsentCaseStatus>
                    status: <strong>{translateConsentStatus(item.status)}</strong>
                  </ConsentCaseStatus>
                </ConsentCaseButton>
              ))
            )}
          </ConsentListPanel>
          <ConsentDetailPanel className="admin-consent-detail-panel">
            {selectedConsentCase ? (
              <>
                <ConsentTitle>Case {selectedConsentCase.caseId}</ConsentTitle>
                <ConsentDetailRow>
                  Speler: <strong>{selectedConsentCase.playerDisplayName || selectedConsentCase.playerId}</strong> ({selectedConsentCase.playerId})
                </ConsentDetailRow>
                <ConsentDetailRow>
                  Account: <strong>{selectedConsentCase.playerAccountId}</strong>
                </ConsentDetailRow>
                <ConsentDetailRow>
                  Sessie: <strong>{selectedConsentCase.sessionId}</strong>
                </ConsentDetailRow>
                <ConsentDetailRow>
                  Limiet: <strong>{selectedConsentCase.requestedLimit?.amount || '-'} {selectedConsentCase.requestedLimit?.currency || 'EUR'}</strong> / {selectedConsentCase.requestedLimit?.period || '-'}
                </ConsentDetailRow>
                <ConsentDetailRow>
                  Attempt: <strong>{selectedConsentCase.currentAttempt}</strong> | status: <strong>{translateConsentStatus(selectedConsentCase.status)}</strong>
                </ConsentDetailRow>
                <ConsentWarning>
                  Let op: goedkeuren/afwijzen/herstarten/verwijzen vereist een opmerking. Bij goedkeuring moet de limiet op het spelersaccount in het platform-adminproces worden bevestigd en als contactmoment worden vastgelegd.
                </ConsentWarning>
                {REVIEWABLE_CONSENT_STATUSES.has(selectedConsentCase.status) && (
                  <ConsentReasonTextarea
                    className="admin-consent-reason-textarea"
                    value={consentReason}
                    onChange={(e) => setConsentReason(e.target.value)}
                    placeholder="Verplichte opmerking voor actie..."
                    rows={5}
                  />
                )}
                <ConsentActions className="admin-consent-actions">
                  {REVIEWABLE_CONSENT_STATUSES.has(selectedConsentCase.status) && (
                    <>
                      <ConsentActionButton
                        className="admin-consent-action-button admin-consent-action-approve"
                        variant="approve"
                        onClick={() => runConsentAction('approve')}
                        disabled={consentActionLoading}
                      >
                        Goedkeuren
                      </ConsentActionButton>
                      <ConsentActionButton
                        className="admin-consent-action-button admin-consent-action-reject"
                        variant="reject"
                        onClick={() => runConsentAction('reject')}
                        disabled={consentActionLoading}
                      >
                        Afwijzen
                      </ConsentActionButton>
                      <ConsentActionButton
                        className="admin-consent-action-button admin-consent-action-refer"
                        variant="refer"
                        onClick={() => runConsentAction('refer_net_deposit')}
                        disabled={consentActionLoading}
                      >
                        Verwijs naar netto stortingslimiet
                      </ConsentActionButton>
                      <ConsentActionButton
                        className="admin-consent-action-button admin-consent-action-restart"
                        variant="neutral"
                        onClick={() => runConsentAction('restart')}
                        disabled={consentActionLoading}
                      >
                        Herstarten
                      </ConsentActionButton>
                    </>
                  )}
                  <ConsentActionButton
                    className="admin-consent-action-button admin-consent-action-refresh"
                    variant="neutral"
                    onClick={fetchConsentCases}
                    disabled={consentActionLoading}
                  >
                    Vernieuwen
                  </ConsentActionButton>
                </ConsentActions>
                {!REVIEWABLE_CONSENT_STATUSES.has(selectedConsentCase.status) && (
                  <MutedText>
                    Dit dossier is afgerond. Goedkeuren/afwijzen is daarom niet meer beschikbaar.
                  </MutedText>
                )}
              </>
            ) : (
              <MutedText>Selecteer een consent-case.</MutedText>
            )}
          </ConsentDetailPanel>
        </ConsentLayout>
      );
    }
    if (activeSection === 'archive') {
      return (
        <ArchiveSection className="admin-archive-section">
          {filteredSessions.length === 0 ? (
            <MutedText>Geen gearchiveerde chats.</MutedText>
          ) : (
            <ArchiveList>
              {filteredSessions.map((session) => (
                <ArchiveListItem key={session._id}>
                  <ArchiveSessionName>{getSessionUserName(session)}</ArchiveSessionName>
                  <ArchiveSessionId>{session._id}</ArchiveSessionId>
                  {session.retentionStatus === 'auto_redacted' && (
                    <ArchiveStatusBadge kind="auto">
                      Automatisch geredigeerd (&gt; 1 jaar retentie)
                    </ArchiveStatusBadge>
                  )}
                  {session.retentionStatus === 'manual_redacted' && (
                    <ArchiveStatusBadge kind="manual">
                      Handmatig geredigeerd (AVG/GDPR)
                    </ArchiveStatusBadge>
                  )}
                  {isSessionOlderThanRetention(session) &&
                    session.retentionStatus !== 'auto_redacted' &&
                    session.retentionStatus !== 'manual_redacted' && (
                      <ArchiveWarning>
                        Waarschuwing: deze gearchiveerde chat is ouder dan 1 jaar en moet volgens AVG/GDPR
                        worden geredigeerd of verwijderd.
                      </ArchiveWarning>
                    )}
                  <ArchiveActions>
                    <NeutralButton
                      onClick={() => handleArchiveSession(session._id, false)}
                    >
                      De-archiveer
                    </NeutralButton>
                    <ArchiveRedactButton
                      onClick={() => handleRedactArchivedSession(session)}
                    >
                      Redigeer
                    </ArchiveRedactButton>
                    <ArchiveExportButton
                      onClick={() => handleExportSession(session._id)}
                    >
                      Exporteer
                    </ArchiveExportButton>
                  </ArchiveActions>
                </ArchiveListItem>
              ))}
            </ArchiveList>
          )}
        </ArchiveSection>
      );
    }
    if (activeSection === 'players') {
      return (
        <PlayersDirectory className="admin-players-directory">
          <PlayersFiltersRow className="admin-players-filters-row">
            <PlayerDirectoryInput
              className="admin-players-filter-id"
              type="text"
              placeholder="Zoek op ID / UUID..."
              value={playerIdSearch}
              onChange={(e) => setPlayerIdSearch(e.target.value)}
            />
            <PlayerDirectoryInput
              className="admin-players-filter-name"
              type="text"
              placeholder="Zoek op spelersnaam..."
              value={playerNameSearch}
              onChange={(e) => setPlayerNameSearch(e.target.value)}
            />
            <PlayerDirectoryInput
              className="admin-players-filter-email"
              type="text"
              placeholder="Zoek op email..."
              value={playerEmailSearch}
              onChange={(e) => setPlayerEmailSearch(e.target.value)}
            />
          </PlayersFiltersRow>
          {filteredPlayersDirectory.length === 0 ? (
            <MutedText>Geen spelers gevonden voor deze filters.</MutedText>
          ) : (
            <PlayersDirectoryList className="admin-players-directory-list">
              {filteredPlayersDirectory.map((session) => (
                <PlayersDirectoryItem key={session._id}>
                  <div>
                    <SessionName>
                      {getSessionUserName(session)}
                      {session.isArchived && <ArchivedPlayerBadge>archived</ArchivedPlayerBadge>}
                    </SessionName>
                    <PlayersDirectoryMeta>{session._id}</PlayersDirectoryMeta>
                    <PlayersDirectoryMeta>{getSessionEmail(session) || 'Geen email bekend'}</PlayersDirectoryMeta>
                  </div>
                  <div>
                    <NeutralButton
                      onClick={() => {
                        setSelectedUser({ id: session._id, name: getSessionUserName(session) });
                        fetchMessagesForUser(session._id);
                        setActiveSection('chat');
                      }}
                    >
                      Open chat
                    </NeutralButton>
                  </div>
                </PlayersDirectoryItem>
              ))}
            </PlayersDirectoryList>
          )}
        </PlayersDirectory>
      );
    }
    if (selectedUser == null) {
      return (
        <UnreadSection className="admin-unread-section">
          {sessionsWithUnread.length === 0 ? (
            <EmptyUnreadText>Geen spelers met ongelezen berichten</EmptyUnreadText>
          ) : (
            <UnreadList>
              {sessionsWithUnread.map(session => (
                <UnreadListItem key={session._id}>
                  <UnreadSessionButton
                    className="admin-unread-session-button"
                    onClick={() => {
                      setSelectedUser({ id: session._id, name: session.userName || session._id });
                      fetchMessagesForUser(session._id);
                    }}
                  >
                    <span>{getSessionUserName(session)}</span>
                    <UnreadCountBadge>{session.unreadCount}</UnreadCountBadge>
                  </UnreadSessionButton>
                </UnreadListItem>
              ))}
            </UnreadList>
          )}
        </UnreadSection>
      );
    }
    return (
      <>
        <MessageArea ref={messagesContainerRef}>
          <MessageList
            messages={chatMessages}
            currentUserId={"admin"}
            typingUsers={[]}
            isAdminView={true}
            onLoadMore={loadMoreMessages}
            hasMore={hasMore[selectedUser.id]}
          />
          <div ref={chatEndRef} />
        </MessageArea>
        <ChatComposer className="admin-chat-composer">
          <ChatMessageInput
            className="admin-chat-input"
            type="text"
            value={adminMessage}
            onChange={e => setAdminMessage(e.target.value)}
            placeholder={`Bericht voor ${selectedUser.name}`}
            onKeyDown={e => { if (e.key === 'Enter') handleSendToUser(); }}
            disabled={sending}
          />
          <HiddenFileInput
            type="file"
            onChange={handleFileChange}
            disabled={sending}
            id="admin-file-input"
          />
          <FilePickerLabel htmlFor="admin-file-input" className="admin-file-picker-button">
            📎
          </FilePickerLabel>
          <PrimaryButton
            className="admin-send-message-button"
            onClick={handleSendToUser}
            disabled={sending || (!adminMessage.trim() && !selectedFile)}
          >
            Verzenden
          </PrimaryButton>
        </ChatComposer>
        {selectedFile && (
          <SelectedFileInfo>
            Bestand: {selectedFile.name}
            <RemoveFileButton onClick={() => setSelectedFile(null)}>×</RemoveFileButton>
          </SelectedFileInfo>
        )}
      </>
    );
  };

  return (
    <Container className="admin-panel-root">
      <Content className="admin-panel-content">
        <Sidebar className="admin-panel-sidebar">
          <SidebarTitle>Spelers</SidebarTitle>
          <PlayerSearchInput
            className="admin-player-search-input"
            type="text"
            placeholder="Zoek op naam, ID/UUID of email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setVisibleCount(20); }}
          />
          <SidebarSection>
            <PlayerListScroll className="admin-player-list-container" onScroll={handleScroll}>
            <SidebarList className="admin-player-list">
              {visibleSessions.map(session => (
                <SidebarListItem className="admin-player-list-item" key={session._id}>
                  <SidebarButton
                    className="admin-player-select-button"
                    selected={selectedUser?.id === session._id}
                    onClick={() => {
                      setSelectedUser({ id: session._id, name: session.userName || session._id });
                      fetchMessagesForUser(session._id);
                    }}
                  >
                    <SessionName>{getSessionUserName(session)}</SessionName>
                    <SessionIdText>{session._id}</SessionIdText>
                    {(session.unreadCount ?? 0) > 0 && selectedUser?.id !== session._id && (
                      <SidebarUnreadBadge>{session.unreadCount}</SidebarUnreadBadge>
                    )}
                  </SidebarButton>
                </SidebarListItem>
              ))}
            </SidebarList>
            </PlayerListScroll>
          </SidebarSection>
        </Sidebar>
        <MainContent className="admin-panel-main">
          <TopNavigation className="admin-top-navigation admin-main-top-navigation" aria-label="Admin section menu">
            <TopNavigationButton
              className="admin-top-navigation-button admin-top-navigation-button-chat"
              active={activeSection === 'chat'}
              onClick={() => setActiveSection('chat')}
            >
              Chat {totalUnreadInSidebar > 0 ? `(${totalUnreadInSidebar})` : ''}
            </TopNavigationButton>
            <TopNavigationButton
              className="admin-top-navigation-button admin-top-navigation-button-files"
              active={activeSection === 'files'}
              onClick={() => setActiveSection('files')}
            >
              Bestanden
            </TopNavigationButton>
            <TopNavigationButton
              className="admin-top-navigation-button admin-top-navigation-button-consent"
              active={activeSection === 'consent'}
              onClick={() => setActiveSection('consent')}
            >
              Consent-dossiers
            </TopNavigationButton>
            <TopNavigationButton
              className="admin-top-navigation-button admin-top-navigation-button-archive"
              active={activeSection === 'archive'}
              onClick={() => setActiveSection('archive')}
            >
              Archief
            </TopNavigationButton>
            <TopNavigationButton
              className="admin-top-navigation-button admin-top-navigation-button-players"
              active={activeSection === 'players'}
              onClick={() => setActiveSection('players')}
            >
              Spelers
            </TopNavigationButton>
          </TopNavigation>
          <StatsTopBar className="admin-stats-top-bar">
            <TopStatCard className="admin-stats-card admin-stats-card-total-messages">
              <TopStatLabel className="admin-stats-label">Totaal berichten</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.totalMessages}</TopStatValue>
            </TopStatCard>
            <TopStatCard className="admin-stats-card admin-stats-card-active-users">
              <TopStatLabel className="admin-stats-label">Actieve spelers</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.activeUsers}</TopStatValue>
            </TopStatCard>
            <TopStatCard className="admin-stats-card admin-stats-card-messages-today">
              <TopStatLabel className="admin-stats-label">Berichten vandaag</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.messagesToday}</TopStatValue>
            </TopStatCard>
            <TopStatCard className="admin-stats-card admin-stats-card-response-time">
              <TopStatLabel className="admin-stats-label">Gem. reactietijd</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.averageResponseTime}s</TopStatValue>
            </TopStatCard>
            <TopStatCard className="admin-stats-card admin-stats-card-open-sessions">
              <TopStatLabel className="admin-stats-label">Open sessies</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.totalSessions - stats.archivedSessions}</TopStatValue>
            </TopStatCard>
            <TopStatCard className="admin-stats-card admin-stats-card-unread-total">
              <TopStatLabel className="admin-stats-label">Ongelezen totaal</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.unreadTotal}</TopStatValue>
            </TopStatCard>
            <TopStatCard className="admin-stats-card admin-stats-card-files-total">
              <TopStatLabel className="admin-stats-label">Bestanden</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.filesTotal}</TopStatValue>
            </TopStatCard>
            <TopStatCard className="admin-stats-card admin-stats-card-archived-sessions">
              <TopStatLabel className="admin-stats-label">Gearchiveerd</TopStatLabel>
              <TopStatValue className="admin-stats-value">{stats.archivedSessions}</TopStatValue>
            </TopStatCard>
          </StatsTopBar>
          <HeaderRow className="admin-main-header">
            <HeaderTitle className="admin-main-title" ref={chatTitleRef}>
              {renderHeaderTitle()}
            </HeaderTitle>
            <HeaderActions className="admin-main-actions">
              {selectedUser && (activeSection === 'chat' || activeSection === 'files') && (
                <>
                  <NeutralButton
                    onClick={() => handleExportSession(selectedUser.id)}
                  >
                    Exporteer chat
                  </NeutralButton>
                  <NeutralButton
                    onClick={() => handleArchiveSession(selectedUser.id, true)}
                  >
                    Archiveer chat
                  </NeutralButton>
                </>
              )}
              <SoundToggleButton
                className="admin-sound-toggle-button"
                enabled={soundEnabled}
                onClick={toggleSound}
                title={soundEnabled ? 'Geluid uitschakelen' : 'Geluid inschakelen'}
                tabIndex={0}
              >
                {soundEnabled ? (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#007bff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </SoundToggleButton>
            </HeaderActions>
          </HeaderRow>
          {renderActiveSection()}
        </MainContent>
      </Content>
      <audio ref={audioRef} src={`${STATIC_URL}/notification.mp3`} preload="auto">
        <track kind="captions" srcLang="nl" label="Notificatiegeluid" />
      </audio>
    </Container>
  );
}; 
